import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck, Upload, Camera, CheckCircle2, XCircle, Clock,
  RefreshCw, ArrowRight, IdCard, FileText, Sparkles, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { id: "national_id", label: "National ID (NRC)", icon: IdCard },
  { id: "passport", label: "Passport", icon: FileText },
  { id: "drivers_license", label: "Driver's Licence", icon: FileText },
];

// Extracted outside the parent function so React doesn't unmount the whole
// subtree on every re-render of Verify. Pure presentational, memoised.
const Stepper = React.memo(function Stepper({ current }) {
  const steps = ["Document", "Selfie", "Review"];
  const idx = { doc: 0, selfie: 1, submitting: 2, result: 2 }[current] ?? 0;
  return (
    <div className="mb-8 flex items-center justify-center gap-3">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-2 ${i <= idx ? "text-copper" : "text-slate-400"}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${i <= idx ? "bg-copper text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</div>
            <span className="hidden text-sm font-semibold sm:inline">{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-0.5 w-8 ${i < idx ? "bg-copper" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
});

export default function Verify() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("intro"); // intro | doc | selfie | submitting | result
  const [docType, setDocType] = useState("national_id");
  const [docPath, setDocPath] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [selfiePath, setSelfiePath] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const streamRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/kyc/status`);
      setStatus(data);
      if (data.status && data.status !== "unverified") {
        setStep("result");
        setResult({ status: data.status, ai_verdict: data.ai_verdict, reason: data.reason });
      }
    } catch (e) {
      if (e.response?.status === 401) nav("/");
    }
  }, [nav]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => () => stopCamera(), []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/uploads/image`, fd);
    return data.path;
  };

  const onDocFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const path = await uploadFile(f);
      setDocPath(path);
      setDocPreview(URL.createObjectURL(f));
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; }
      setCameraOn(true);
    } catch (err) {
      toast.error("Could not access camera. Please allow permission or upload a photo.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  const captureSelfie = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setUploading(true);
    try {
      const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.9));
      const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: "image/jpeg" });
      const path = await uploadFile(file);
      setSelfiePath(path);
      setSelfiePreview(URL.createObjectURL(blob));
      stopCamera();
      toast.success("Selfie captured");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save selfie");
    } finally { setUploading(false); }
  };

  const onSelfieFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const path = await uploadFile(f);
      setSelfiePath(path);
      setSelfiePreview(URL.createObjectURL(f));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!docPath || !selfiePath) return toast.error("Upload both photos first");
    setSubmitting(true); setStep("submitting");
    try {
      const { data } = await axios.post(`${API}/kyc/submit`, {
        document_type: docType, document_path: docPath, selfie_path: selfiePath,
      });
      setResult({ status: data.status, ai_verdict: data.ai_verdict });
      setStep("result");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
      setStep("selfie");
    } finally { setSubmitting(false); }
  };

  const restart = () => {
    setStep("intro"); setDocPath(null); setDocPreview(null);
    setSelfiePath(null); setSelfiePreview(null); setResult(null);
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <ShieldCheck className="mx-auto h-14 w-14 text-copper" />
        <h1 className="mt-4 font-display text-3xl font-bold">Sign in to verify your identity</h1>
        <Link to="/" className="btn-primary mt-6 inline-block text-sm">Go to home</Link>
      </div>
    );
  }

  // ---------------- STEPS ----------------

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 lg:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-copper/10 text-copper">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Identity verification</h1>
          <p className="text-sm text-slate-600">Confirm your identity to unlock higher booking limits and a verified badge.</p>
        </div>
      </div>

      {step === "intro" && (
        <div className="space-y-6">
          <div className="rounded-3xl bg-white p-6 card-shadow">
            <div className="flex items-start gap-4">
              <Sparkles className="mt-1 h-6 w-6 text-copper" />
              <div>
                <div className="font-display text-lg font-bold">One-time identity check</div>
                <p className="mt-2 text-sm text-slate-700">
                  EasyStay verifies every new account once at sign-up to keep the community safe. You&apos;ll upload a photo of your ID and take a quick selfie — takes about a minute.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" /> Verified badge on your profile</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" /> Trusted status with hosts</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" /> Your documents are stored securely and never shown to hosts</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="rounded-3xl bg-white p-6 card-shadow">
            <div className="font-display text-lg font-bold">What you&apos;ll need</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {DOC_TYPES.map((d) => (
                <button key={d.id} onClick={() => { setDocType(d.id); setStep("doc"); }}
                  data-testid={`doc-type-${d.id}`}
                  className={`rounded-2xl border-2 p-4 text-left transition ${docType === d.id ? "border-copper bg-copper/5" : "border-slate-200 hover:border-copper"}`}>
                  <d.icon className="h-6 w-6 text-copper" />
                  <div className="mt-2 font-semibold text-slate-900">{d.label}</div>
                  <div className="mt-1 text-xs text-slate-500">Clear photo of the front page</div>
                </button>
              ))}
            </div>
          </div>
          <button data-testid="verify-start-btn" onClick={() => setStep("doc")} className="btn-primary w-full text-sm">Start verification <ArrowRight className="ml-1 inline h-4 w-4" /></button>
        </div>
      )}

      {step === "doc" && (
        <div>
          <Stepper current="doc" />
          <div className="rounded-3xl bg-white p-6 card-shadow">
            <div className="text-xs uppercase tracking-widest text-slate-500">Step 1 · Document</div>
            <h2 className="mt-1 font-display text-2xl font-bold text-slate-900">Upload your {DOC_TYPES.find((d) => d.id === docType)?.label}</h2>
            <p className="mt-2 text-sm text-slate-600">Ensure the entire document is visible, in focus, and not glare-covered.</p>

            <label htmlFor="doc-file" className="mt-6 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center hover:border-copper">
              {docPreview ? (
                <img src={docPreview} alt="doc" className="max-h-64 rounded-xl" />
              ) : (
                <>
                  <Upload className="h-10 w-10 text-slate-400" />
                  <div className="mt-3 text-sm font-medium text-slate-700">{uploading ? "Uploading…" : "Tap to upload photo"}</div>
                  <div className="text-xs text-slate-500">JPG / PNG / WebP · Max 8MB</div>
                </>
              )}
              <input id="doc-file" data-testid="doc-file-input" type="file" accept="image/*" className="hidden" onChange={onDocFile} />
            </label>

            <div className="mt-6 flex justify-between gap-3">
              <button onClick={() => setStep("intro")} className="btn-outline text-sm">Back</button>
              <button data-testid="doc-next-btn" onClick={() => setStep("selfie")} disabled={!docPath} className="btn-primary text-sm">Continue <ArrowRight className="ml-1 inline h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      {step === "selfie" && (
        <div>
          <Stepper current="selfie" />
          <div className="rounded-3xl bg-white p-6 card-shadow">
            <div className="text-xs uppercase tracking-widest text-slate-500">Step 2 · Live selfie</div>
            <h2 className="mt-1 font-display text-2xl font-bold text-slate-900">Take a selfie for face matching</h2>
            <p className="mt-2 text-sm text-slate-600">Look straight at the camera, in good light. We compare this to your document photo.</p>

            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500">Camera</div>
                <div className="mt-2 aspect-[4/3] overflow-hidden rounded-xl bg-black">
                  {cameraOn ? (
                    <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
                  ) : selfiePreview ? (
                    <img src={selfiePreview} alt="selfie" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-white/60">
                      <Camera className="h-10 w-10" />
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>
                <div className="mt-3 flex gap-2">
                  {!cameraOn ? (
                    <button data-testid="camera-start-btn" onClick={startCamera} className="btn-outline flex-1 text-sm">
                      <Camera className="mr-1 inline h-4 w-4" /> {selfiePreview ? "Retake" : "Open camera"}
                    </button>
                  ) : (
                    <>
                      <button onClick={stopCamera} className="btn-outline flex-1 text-sm">Cancel</button>
                      <button data-testid="camera-capture-btn" onClick={captureSelfie} disabled={uploading} className="btn-primary flex-1 text-sm">
                        <Camera className="mr-1 inline h-4 w-4" /> Capture
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500">Or upload a selfie</div>
                <label htmlFor="selfie-file" className="mt-2 flex aspect-[4/3] cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white hover:border-copper">
                  <div className="text-center">
                    <Upload className="mx-auto h-8 w-8 text-slate-400" />
                    <div className="mt-1 text-xs text-slate-500">Choose from gallery</div>
                  </div>
                  <input id="selfie-file" data-testid="selfie-file-input" type="file" accept="image/*" className="hidden" onChange={onSelfieFile} />
                </label>
                <div className="mt-3 rounded-xl bg-copper/5 p-3 text-xs text-slate-700">
                  <AlertCircle className="mr-1 inline h-3.5 w-3.5 text-copper" />
                  Tip: remove hats, glasses and masks. Face should fill the frame.
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-between gap-3">
              <button onClick={() => setStep("doc")} className="btn-outline text-sm">Back</button>
              <button data-testid="verify-submit-btn" onClick={submit} disabled={!selfiePath || submitting} className="btn-primary text-sm">
                Submit for review <ArrowRight className="ml-1 inline h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "submitting" && (
        <div className="rounded-3xl bg-white p-16 text-center card-shadow">
          <div className="mx-auto h-14 w-14 animate-spin rounded-full border-4 border-copper border-t-transparent" />
          <h2 className="mt-6 font-display text-2xl font-bold">Running AI face-match analysis…</h2>
          <p className="mt-2 text-sm text-slate-600">This usually takes 5–15 seconds. Please don&apos;t close this page.</p>
        </div>
      )}

      {step === "result" && result && (
        <div>
          <Stepper current="result" />
          {result.status === "approved" && (
            <div data-testid="kyc-approved-panel" className="rounded-3xl border-2 border-green-200 bg-white p-8 card-shadow">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-100 text-green-700">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-green-700">Approved</div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">You&apos;re verified!</h2>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-700">You now have a verified badge on your profile. Welcome to EasyStay.</p>
              {result.ai_verdict && (
                <div className="mt-4 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                  <div>AI match score: <span className="font-semibold text-slate-900">{result.ai_verdict.match_score}/100</span></div>
                  <div className="mt-1">{result.ai_verdict.notes}</div>
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <Link to="/search" className="btn-primary text-sm">Find a stay</Link>
                <Link to="/my/bookings" className="btn-outline text-sm">My bookings</Link>
              </div>
            </div>
          )}
          {result.status === "pending" && (
            <div data-testid="kyc-pending-panel" className="rounded-3xl border-2 border-amber-200 bg-white p-8 card-shadow">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-700">Under review</div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">We need a moment</h2>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-700">Our AI needs a manual look at your submission. A team member will review within 24 hours. You&apos;ll see your status update here.</p>
              {result.ai_verdict && (
                <div className="mt-4 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                  <div>AI match score: <span className="font-semibold text-slate-900">{result.ai_verdict.match_score}/100</span></div>
                  <div className="mt-1">{result.ai_verdict.notes}</div>
                </div>
              )}
              <button onClick={loadStatus} className="btn-outline mt-6 text-sm"><RefreshCw className="mr-1 inline h-3.5 w-3.5" /> Refresh status</button>
            </div>
          )}
          {result.status === "rejected" && (
            <div data-testid="kyc-rejected-panel" className="rounded-3xl border-2 border-rose-200 bg-white p-8 card-shadow">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-700">
                  <XCircle className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-rose-700">Not approved</div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">We couldn&apos;t verify this submission</h2>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-700">{result.reason || result.ai_verdict?.notes || "The document or selfie didn&apos;t meet our checks."}</p>
              <button onClick={restart} data-testid="kyc-retry-btn" className="btn-primary mt-6 text-sm">Try again</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
